<template>
  <div class="history-area" data-app>
    <div class="management-section-wrapper">
      <v-data-table
        :headers="headers"
        :items="reports"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :items-per-page.sync="paginationHistoryArea.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon, 'items-per-page-options':rowsPerPageItems }"
        @pagination="setRowsOnLocalStorage(dataTableHistoryAreaId,paginationHistoryArea.rowsPerPage)"
      >
        <template v-slot:item="props">
          <tr>
            <td>
              {{
                props.item && props.item.finalized_at
                  ? props.item.finalized_at
                  : props.item.created_at
              }}
            </td>

            <td>{{ getWorkspaceName(props.item.workspace_id) }}</td>

            <td>{{ getReportTypeLabel(props.item) }}</td>

            <td style="text-transform: uppercase">
              {{ getStepAndStatus(props.item) }}
            </td>

            <td>
              <v-layout row justify-end align-center>
                <management-table-button
                  v-if="isSuccessful(props.item)"
                  button-type="Download"
                  :file-url="props.item.file_url_public"
                  :without-label="true"
                  style="
                    height: 25px;
                    min-width: unset;
                    padding: 6px 6px 6px 8px;
                    margin-right: 35px;
                  "
                  :disabled="!userHasManagementPermission('export_show')"
                  @buttonClick="downloadExport(props.item.file_url_public, $event)"
                />
                <v-flex shrink>
                  <app-datatable-action-buttons
                    v-if="isDeletable(props.item)"
                    :user-can-edit="false"
                    :user-can-delete="true"
                    :visibility-hover="false"
                    @delete="askConfirmationToDeleteItem(props.item)"
                    style="padding-right: 10px"
                  />
                </v-flex>
                <v-btn
                  v-if="!isDeletable(props.item)"
                  x-small
                  text
                  color="accent"
                  @click="cancelExport(props.item)"
                >
                  <span style="font-size: 10px">{{ $t("Cancel") }}</span>
                </v-btn>
                <v-progress-circular
                  v-if="!isDeletable(props.item)"
                  indeterminate
                  size="20"
                  width="3"
                  color="accent"
                  class="mx-3"
                />
              </v-layout>
            </td>
          </tr>
        </template>

        <template v-slot:item.include_buildings="{ item }">
          <v-checkbox v-model="item.include_buildings" disabled />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" style="width: 100%" />
          <v-alert v-else class="data-table-no-data" icon="warning">
            {{ $t("NoDataExport") }}
          </v-alert>
        </template>
      </v-data-table>
    </div>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteReport')"
      :content="
        $t('DeleteConfirmation') +
        ' ' +
        $tc('ExportNoun', 1) +
        ' ' +
        itemToDelete.id +
        '?'
      "
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteExport"
    />
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { mapActions } from "vuex";
import ManagementTableButton from "../../../../../Management/ManagementTableButton";
import ManagementButton from "../../../../../Management/ManagementButton";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import { exportReportHistoryMixin } from "@/mixins/exportReportHistoryMixin";

export default {
  name: "ReportHistoryArea",

  components: {
    AppDatatableActionButtons,
    AppDialogBox,
    ManagementButton,
    ManagementTableButton,
  },

  mixins: [exportReportHistoryMixin],

  props: {
    historyFilter: {
      type: String,
      required: true,
    },

    selectedWorkspaceUUID: {
      type: String,
    },
  },

  data() {
    return {
      workspaceOptions: [],
      reportTypeOptions: null,
      dataTableHistoryAreaId: "dataTableHistoryAreaId" ,
      paginationHistoryArea: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableHistoryAreaId")
      },
    };
  },

  computed: {
    headers() {
      return [
        {
          text: this.$i18n.t("Date"),
          align: "left",
          value: "created_at",
          width: "15%",
        },
        {
          text: this.$i18n.tc("Workspace", 1),
          align: "left",
          value: "workspace_id",
          width: "20%",
        },
        {
          text: this.$i18n.tc("Type", 1),
          align: "left",
          value: "version",
          width: "20%",
        },
        {
          text: this.$i18n.t("Step") + ": " + this.$i18n.t("Status"),
          align: "left",
          value: "status",
          width: "15%",
        },
        { text: "", align: "center", sortable: false, width: "15%" },
      ];
    },
  },

  watch: {
    selectedWorkspaceUUID(newValue) {
      if (newValue && !this.isAdmin) {
        this.getWorkspaceReports();
      }
    },
  },

  created() {
    this.loadWorkspaceOptions();
    this.getReportTypeOptions();
  },

  mounted() {
    this.$nextTick(() => {
      this.getReports();
      this.interval = setInterval(() => {
        this.getReports();
      }, 10000);
    });

    bus.$on("add-home-report-file-to-list", (newReport) =>
      this.addReportToList(newReport)
    );
  },

  activated() {
    this.interval = setInterval(() => {
      this.getReports();
    }, 10000);
  },

  deactivated() {
    this.stopHistoryRequests();
  },

  destroyed() {
    this.stopHistoryRequests();
    bus.$off("add-home-report-file-to-list");
  },

  methods: {
    ...mapActions([
      "GET_EXPORTS_BY_TYPE_AND_WORKSPACE",
      "fetchWorkspaceByUUID",
      "fetchUserAndCompanyWorkspaces",
      "fetchAllWorkspaces",
      "fetchExportMapperTemplates",
    ]),

    loadWorkspaceOptions() {
      let action = "";
      let params = {};
      if (this.isSuperAdmin || this.isAdmin) {
        action = "fetchAllWorkspaces";
        params = {
          includes: null,
          fields: "wp_uuid,wp_label,wp_id&filter[wp_access_active]=true",
        };
      } else {
        action = "fetchUserAndCompanyWorkspaces";
      }
      this[action](params)
        .then((workspaces) => {
          this.workspaceOptions = workspaces;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    /**
     * Gets the list/history of the reports from the backend and keeps doing it until all reports are generated.
     */
    getReports() {
      if (this.isAdmin) {
        this.getExports();
      } else if (this.selectedWorkspaceUUID) {
        this.getWorkspaceReports();
      }
    },

    /**
     * Sets the history with a list of the reports of a given type for a given workspace.
     */
    getWorkspaceReports() {
      this.fetchWorkspaceByUUID({
        uuid: this.selectedWorkspaceUUID,
        includes: null,
        fields: "wp_id",
      })
        .then((workspace) => {
          this.workspaceID = workspace.wp_id;
          this.GET_EXPORTS_BY_TYPE_AND_WORKSPACE({
            type: this.historyFilter,
            workspaceID: this.workspaceID,
          })
            .then((reports) => {
              this.reports = reports;
            })
            .catch((error) => console.error(error))
            .then(() => {
              this.loading = false;
            });
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },

    deleteExport() {
      this.removeExportByUUID(this.itemToDelete.uuid)
        .then(() => {
          this.getReports();
          this.deleteDialog = false;
          this.itemToDelete = {};
        })
        .catch((error) => {
          console.log(error);
        });
    },

    /**
     * Sends a request to produce a report with the given data.
     */
    addReportToList(newReport) {
      this.reports.unshift(newReport);
      this.getReports();
    },

    getReportTypeOptions() {
      let options = this.$store.getters.getExportTypes;
      if (!options) {
        options = this.fetchExportMapperTemplates();
      }
      this.reportTypeOptions = options;
    },

    getReportTypeLabel(item) {
      let label = item.version;
      if (this.reportTypeOptions) {
        let option = this.reportTypeOptions.find((type) => type.version === item.version);
        if (option) {
          label = option.name;
        }
      }
      return label;
    },
  },
};
</script>

<style scoped>
.history-area {
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Reports/History/HistoryArea.vue